import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { fetchPreferences } from '../../hooks/usePreferences';
import { firstAllowedPage } from '../../lib/permissions';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AuthResponse, authService, LoginDto, needsTwoFactor } from '../../services/auth.service';
import { useAuthStore } from '../../store/auth.store';
import {
  ArrowPathIcon,
  ChartBarSquareIcon,
  CubeIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

const HIGHLIGHTS = [
  { icon: CubeIcon, text: 'Inventory, purchase, sales and production in one place' },
  { icon: ChartBarSquareIcon, text: 'GST-ready documents and live reports' },
  { icon: ShieldCheckIcon, text: 'Role-based access, audit trail and two-step sign-in' },
];

const TEST_ACCOUNTS = [
  { role: 'Admin', email: 'admin@erp.com', password: 'admin123' },
  { role: 'Manager', email: 'manager@erp.com', password: 'manager123' },
  { role: 'User', email: 'user@erp.com', password: 'user123' },
];

const fieldClass =
  'block w-full rounded-lg border bg-white py-2.5 pl-10 pr-3 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-indigo-500/30';

const Spinner = () => <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />;

// The frame of both sign-in steps: the brand on the left on wide screens, the form on the right
const AuthShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-screen bg-gray-50">
    <aside className="relative hidden w-[46%] max-w-xl flex-col justify-between overflow-hidden bg-gradient-to-br from-indigo-700 via-indigo-600 to-violet-600 p-12 text-white lg:flex">
      <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-white/5" />
      <div className="relative flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 text-lg font-bold ring-1 ring-white/25">R</span>
        <span className="text-xl font-bold tracking-tight">RightFitERP</span>
      </div>
      <div className="relative">
        <h1 className="text-3xl font-semibold leading-tight">Run the whole factory from one screen.</h1>
        <p className="mt-3 max-w-md text-sm text-indigo-100">The manufacturing ERP for Indian SMEs, from the purchase order to the GST invoice.</p>
        <ul className="mt-8 space-y-4">
          {HIGHLIGHTS.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-3 text-sm text-indigo-50">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15 ring-1 ring-white/20">
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="pt-1.5">{text}</span>
            </li>
          ))}
        </ul>
      </div>
      <p className="relative text-xs text-indigo-200">© {new Date().getFullYear()} Gunadhya Software</p>
    </aside>
    <main className="flex flex-1 items-center justify-center px-4 py-10 sm:px-8">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2 lg:hidden">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-600 text-base font-bold text-white">R</span>
          <span className="text-xl font-bold tracking-tight text-gray-900">RightFitERP</span>
        </div>
        {children}
      </div>
    </main>
  </div>
);

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  // Set once the password was right on an account with two-step sign-in: the code is asked next
  const [challenge, setChallenge] = useState<string | null>(null);
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginDto>();

  const finish = (data: AuthResponse) => {
    setUser(data.user);
    toast.success('Login successful!');
    // Open on the page the user chose in Settings; the dashboard when that cannot be read
    // ...or the first page the role allows, when that one is not open to this user
    const permissions = (data.user as { permissions?: string[] }).permissions;
    if (data.user.portal) {
      navigate('/portal');
      return;
    }
    fetchPreferences()
      .then((preferences) => navigate(firstAllowedPage(permissions, preferences.startPage || '/dashboard')))
      .catch(() => navigate(firstAllowedPage(permissions, '/dashboard')));
  };

  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (data) => {
      if (needsTwoFactor(data)) {
        setChallenge(data.challengeToken);
        setUseRecovery(false);
        setCode('');
        setCodeError('');
        return;
      }
      finish(data);
    },
    onError: (error: any) => {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || 'Login failed');
    },
  });

  const onSubmit = (data: LoginDto) => {
    loginMutation.mutate(data);
  };

  const verifyMutation = useMutation({
    mutationFn: authService.verifyTwoFactor,
    onSuccess: finish,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'The code could not be checked';
      setCode('');
      // An expired sign-in starts again from the password
      if (/expired/i.test(String(message))) {
        setChallenge(null);
        toast.error(String(message));
        return;
      }
      setCodeError(Array.isArray(message) ? message.join(', ') : String(message));
    },
  });

  const submitCode = (event: React.FormEvent) => {
    event.preventDefault();
    const value = code.trim();
    const valid = useRecovery ? value.replace(/[\s-]/g, '').length === 8 : /^\d{6}$/.test(value.replace(/\s/g, ''));
    if (!valid) {
      setCodeError(useRecovery ? 'Enter one of your recovery codes, like ABCD-EFGH' : 'Enter the six digits the authenticator app shows');
      return;
    }
    setCodeError('');
    const challengeToken = challenge as string;
    verifyMutation.mutate(useRecovery ? { challengeToken, recoveryCode: value } : { challengeToken, code: value.replace(/\s/g, '') });
  };

  if (challenge) {
    return (
      <AuthShell>
        <form onSubmit={submitCode} noValidate className="rounded-2xl bg-white p-6 shadow-xl shadow-gray-200/60 ring-1 ring-gray-200 sm:p-8">
          <div className="flex flex-col items-center text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 ring-8 ring-indigo-50/50">
              <DevicePhoneMobileIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
            </span>
            <h2 className="mt-4 text-2xl font-semibold text-gray-900">Two-step sign-in</h2>
            <p className="mt-2 text-sm text-gray-600">
              {useRecovery
                ? 'Enter one of the recovery codes you saved when you turned two-step sign-in on. Each one works once.'
                : 'Open the authenticator app on your phone and enter the six-digit code it shows for RightFitERP.'}
            </p>
          </div>
          <div className="mt-6">
            <label htmlFor="two-factor-code" className="block text-sm font-medium text-gray-700">
              {useRecovery ? 'Recovery code' : 'Code'}
            </label>
            <input
              id="two-factor-code"
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
                setCodeError('');
              }}
              autoFocus
              autoComplete="one-time-code"
              inputMode={useRecovery ? 'text' : 'numeric'}
              maxLength={useRecovery ? 12 : 7}
              placeholder={useRecovery ? 'ABCD-EFGH' : '123456'}
              aria-invalid={!!codeError}
              className={`mt-1.5 block w-full rounded-lg border bg-white px-3 py-3 text-center font-mono text-xl tracking-[0.4em] text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 ${codeError ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-indigo-500'}`}
            />
            {codeError && (
              <p className="mt-1.5 text-sm text-red-600" role="alert">
                {codeError}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={verifyMutation.isPending}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
          >
            {verifyMutation.isPending && <Spinner />}
            {verifyMutation.isPending ? 'Checking…' : 'Verify'}
          </button>
          <div className="mt-4 flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => {
                setUseRecovery(!useRecovery);
                setCode('');
                setCodeError('');
              }}
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {useRecovery ? 'Use the authenticator app' : 'Use a recovery code'}
            </button>
            <button type="button" onClick={() => setChallenge(null)} className="text-gray-500 hover:text-gray-700">
              Back
            </button>
          </div>
          <p className="mt-6 border-t border-gray-100 pt-4 text-xs text-gray-500">Lost your phone and your recovery codes? An administrator can turn two-step sign-in off for you on the Users page.</p>
        </form>
      </AuthShell>
    );
  }

  const fillTestCredentials = (email: string, password: string) => {
    setValue('email', email, { shouldValidate: true });
    setValue('password', password, { shouldValidate: true });
  };

  return (
    <AuthShell>
      <div className="rounded-2xl bg-white p-6 shadow-xl shadow-gray-200/60 ring-1 ring-gray-200 sm:p-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Welcome back</h2>
          <p className="mt-1 text-sm text-gray-600">
            Sign in to your account, or{' '}
            <Link to="/register" className="font-medium text-indigo-600 hover:text-indigo-500">
              create a new account
            </Link>
          </p>
        </div>

        <form className="mt-6 space-y-5" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <div className="relative mt-1.5">
              <EnvelopeIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                id="email"
                type="email"
                autoComplete="email"
                aria-invalid={!!errors.email}
                className={`${fieldClass} ${errors.email ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-indigo-500'}`}
                placeholder="you@company.com"
              />
            </div>
            {errors.email && <p className="mt-1.5 text-sm text-red-600">{errors.email.message}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="relative mt-1.5">
              <LockClosedIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                {...register('password', { required: 'Password is required' })}
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                aria-invalid={!!errors.password}
                className={`${fieldClass} pr-11 ${errors.password ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-indigo-500'}`}
                placeholder="Your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && <p className="mt-1.5 text-sm text-red-600">{errors.password.message}</p>}
          </div>

          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
          >
            {loginMutation.isPending && <Spinner />}
            {loginMutation.isPending ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <div className="mt-6 rounded-xl border border-dashed border-indigo-200 bg-indigo-50/50 p-3">
          <h3 className="px-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">Test Credentials (click to fill)</h3>
          <div className="mt-2 grid gap-1.5">
            {TEST_ACCOUNTS.map((account) => (
              <button
                key={account.email}
                type="button"
                onClick={() => fillTestCredentials(account.email, account.password)}
                className="flex min-w-0 items-center gap-3 rounded-lg bg-white px-3 py-2 text-left text-xs ring-1 ring-indigo-100 transition hover:bg-indigo-50 hover:ring-indigo-300"
              >
                <UserCircleIcon className="h-5 w-5 shrink-0 text-indigo-500" aria-hidden="true" />
                <span className="w-16 shrink-0 font-semibold text-gray-900">{account.role}</span>
                <span className="min-w-0 flex-1 font-mono text-gray-600">
                  <span className="block sm:inline">{account.email}</span>
                  <span className="hidden sm:inline"> / </span>
                  <span className="block sm:inline">{account.password}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </AuthShell>
  );
};

export default Login;